import { PageHeading } from "@/components/ui/page";
import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Download, FileCheck, Send, Check, X, Pencil } from "lucide-react";
import { getApiErrorPayload, quotationsAPI } from "@/lib/api";
import { useTranslation } from "react-i18next";
import { PermissionGate } from "@/features/auth/components/PermissionGate";
import { useToast } from "@/hooks/use-toast";
import { formatDate, formatNumber } from "@/lib/utils";
import { Loading } from "@/components/ui/loader";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Price } from "@/components/ui/price";
import { confirmAction } from "@/components/ui/confirm-dialog";
import { useCurrency } from "@/hooks/useCurrency";

const statusVariant = (status: string): "default" | "secondary" | "destructive" | "outline" | "success" | "warning" => {
	switch (status) {
		case "converted":
		case "accepted":
			return "success";
		case "sent":
			return "default";
		case "rejected":
		case "cancelled":
			return "destructive";
		case "expired":
			return "warning";
		default:
			return "secondary";
	}
};

const QuotationDetailPage: React.FC = () => {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const { t } = useTranslation();
	const { toast } = useToast();
	const currency = useCurrency();

	const [quotation, setQuotation] = useState<any>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [busy, setBusy] = useState(false);

	const fetchQuotation = useCallback(async () => {
		if (!id) return;
		try {
			setIsLoading(true);
			setQuotation(await quotationsAPI.getById(id));
		} catch (error) {
			const payload = getApiErrorPayload(error);
			toast({
				title: t("common.error"),
				description: t(payload.key, (payload.params ?? {}) as Record<string, string>),
				variant: "destructive",
			});
			navigate("/quotations");
		} finally {
			setIsLoading(false);
		}
	}, [id, navigate, t, toast]);

	useEffect(() => {
		fetchQuotation();
	}, [fetchQuotation]);

	const runAction = async (fn: () => Promise<any>, successKey: string) => {
		try {
			setBusy(true);
			await fn();
			toast({ title: t("common.success"), description: t(successKey) });
			await fetchQuotation();
		} catch (error) {
			const payload = getApiErrorPayload(error);
			toast({
				title: t("common.error"),
				description: t(payload.key, (payload.params ?? {}) as Record<string, string>),
				variant: "destructive",
			});
		} finally {
			setBusy(false);
		}
	};

	const changeStatus = (status: string) =>
		runAction(() => quotationsAPI.updateStatus(id!, status), "quotations.messages.statusUpdated");

	const convert = async () => {
		if (
			!(await confirmAction(t("quotations.confirmConvert"), {
				confirmLabel: t("quotations.detail.convert"),
				destructive: false,
			}))
		)
			return;
		try {
			setBusy(true);
			const res = await quotationsAPI.convert(id!);
			toast({ title: t("common.success"), description: t("quotations.messages.converted") });
			if (res?.saleId) navigate(`/sales/${res.saleId}`);
			else await fetchQuotation();
		} catch (error) {
			const payload = getApiErrorPayload(error);
			toast({
				title: t("common.error"),
				description: t(payload.key, (payload.params ?? {}) as Record<string, string>),
				variant: "destructive",
			});
		} finally {
			setBusy(false);
		}
	};

	const downloadPdf = async () => {
		if (!id) return;
		try {
			const blob = await quotationsAPI.downloadPdf(id);
			const url = URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.download = `quotation-${quotation?.quotationNumber ?? id}.pdf`;
			document.body.appendChild(link);
			link.click();
			link.remove();
			URL.revokeObjectURL(url);
		} catch (error) {
			const payload = getApiErrorPayload(error);
			toast({
				title: t("common.error"),
				description: t(payload.key, (payload.params ?? {}) as Record<string, string>),
				variant: "destructive",
			});
		}
	};

	if (isLoading) return <Loading />;
	if (!quotation) return null;

	const c = quotation.customer;
	const customerName = c
		? [c.firstName, c.lastName].filter(Boolean).join(" ") || c.companyName || c.phone || `#${c.id}`
		: t("quotations.walkIn");
	const isTerminal = ["converted", "cancelled", "rejected", "expired"].includes(quotation.status);
	const canConvert = ["draft", "sent", "accepted"].includes(quotation.status);

	return (
		<div className="container mx-auto py-6 space-y-6">
			<div className="flex flex-wrap items-center justify-between gap-4">
				<div className="flex items-center gap-3">
					<div>
						<PageHeading className="flex flex-wrap items-center gap-3">
							{quotation.quotationNumber}
							<Badge variant={statusVariant(quotation.status)}>
								{t(`quotations.status.${quotation.status}`)}
							</Badge>
						</PageHeading>
						<p className="text-muted-foreground">{t("quotations.detail.description")}</p>
					</div>
				</div>
				<div className="flex flex-wrap items-center gap-2">
					<Button variant="outline" onClick={downloadPdf}>
						<Download className="h-4 w-4 mx-2" />
						{t("quotations.detail.download")}
					</Button>
					{!isTerminal && (
						<PermissionGate permission="sales.update">
							<Button variant="outline" onClick={() => navigate(`/quotations/${id}/edit`)}>
								<Pencil className="h-4 w-4 mx-2" />
								{t("quotations.detail.edit")}
							</Button>
						</PermissionGate>
					)}
					{!isTerminal && (
						<PermissionGate permission="sales.update">
							{quotation.status === "draft" && (
								<Button variant="outline" disabled={busy} onClick={() => changeStatus("sent")}>
									<Send className="h-4 w-4 mx-2" />
									{t("quotations.detail.markSent")}
								</Button>
							)}
							{(quotation.status === "draft" || quotation.status === "sent") && (
								<>
									<Button variant="outline" disabled={busy} onClick={() => changeStatus("accepted")}>
										<Check className="h-4 w-4 mx-2" />
										{t("quotations.detail.markAccepted")}
									</Button>
									<Button
										variant="outline"
										disabled={busy}
										onClick={async () => {
											if (
												await confirmAction(t("quotations.confirmReject"), {
													confirmLabel: t("quotations.detail.markRejected"),
												})
											)
												changeStatus("rejected");
										}}
									>
										<X className="h-4 w-4 mx-2" />
										{t("quotations.detail.markRejected")}
									</Button>
								</>
							)}
						</PermissionGate>
					)}
					{canConvert && (
						<PermissionGate permission="sales.create">
							<Button disabled={busy} onClick={convert}>
								<FileCheck className="h-4 w-4 mx-2" />
								{t("quotations.detail.convert")}
							</Button>
						</PermissionGate>
					)}
				</div>
			</div>

			{quotation.convertedSaleId && (
				<Card>
					<CardContent className="pt-6">
						<p className="text-sm">
							{t("quotations.detail.convertedTo")}{" "}
							<Button
								variant="link"
								className="px-1"
								onClick={() => navigate(`/sales/${quotation.convertedSaleId}`)}
							>
								#{quotation.convertedSaleId}
							</Button>
						</p>
					</CardContent>
				</Card>
			)}

			<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
				<div className="lg:col-span-2 space-y-6">
					<Card>
						<CardHeader>
							<CardTitle>{t("quotations.detail.items")}</CardTitle>
						</CardHeader>
						<CardContent>
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>{t("quotations.add.products")}</TableHead>
										<TableHead>{t("quotations.add.quantity")}</TableHead>
										<TableHead>{t("quotations.add.unitPrice")}</TableHead>
										<TableHead>{t("quotations.add.discount")}</TableHead>
										<TableHead>{t("quotations.add.taxPercent")}</TableHead>
										<TableHead className="text-start">{t("quotations.add.total")}</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{(quotation.items || []).map((it: any) => (
										<TableRow key={it.id}>
											<TableCell>{it.product?.name ?? `#${it.productId}`}</TableCell>
											<TableCell>{formatNumber(Number(it.quantity))}</TableCell>
											<TableCell>
												<Price value={it.unitPrice} currency={currency} />
											</TableCell>
											<TableCell>
												{Number(it.discountRate) > 0
													? `${formatNumber(Number(it.discountRate))}%`
													: "-"}
											</TableCell>
											<TableCell>
												{Number(it.taxRate) > 0 ? `${formatNumber(Number(it.taxRate))}%` : "-"}
											</TableCell>
											<TableCell className="text-start">
												<Price value={it.totalAmount} currency={currency} />
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</CardContent>
					</Card>
				</div>

				<div className="space-y-6">
					<Card>
						<CardHeader>
							<CardTitle>{t("quotations.detail.info")}</CardTitle>
						</CardHeader>
						<CardContent className="space-y-3 text-sm">
							<div className="flex justify-between">
								<span className="text-muted-foreground">{t("quotations.table.customer")}</span>
								<span>{customerName}</span>
							</div>
							<div className="flex justify-between">
								<span className="text-muted-foreground">{t("quotations.table.date")}</span>
								<span>{quotation.createdAt ? formatDate(quotation.createdAt) : "-"}</span>
							</div>
							<div className="flex justify-between">
								<span className="text-muted-foreground">{t("quotations.table.validUntil")}</span>
								<span>{quotation.validUntil ? formatDate(quotation.validUntil) : "-"}</span>
							</div>
							{quotation.notes && (
								<div className="pt-2 border-t">
									<span className="text-muted-foreground">{t("quotations.add.notes")}</span>
									<p className="mt-1">{quotation.notes}</p>
								</div>
							)}
						</CardContent>
					</Card>
					<Card>
						<CardHeader>
							<CardTitle>{t("quotations.add.summary")}</CardTitle>
						</CardHeader>
						<CardContent className="space-y-3 text-sm">
							<div className="flex justify-between">
								<span>{t("quotations.add.subtotal")}</span>
								<span>
									<Price value={quotation.subtotal} currency={currency} />
								</span>
							</div>
							<div className="flex justify-between">
								<span>{t("quotations.add.discount")}</span>
								<span className="text-destructive">
									-<Price value={quotation.discountAmount} currency={currency} />
								</span>
							</div>
							<div className="flex justify-between">
								<span>{t("quotations.add.tax")}</span>
								<span>
									<Price value={quotation.taxAmount} currency={currency} />
								</span>
							</div>
							<div className="border-t pt-3 flex justify-between text-lg font-bold">
								<span>{t("quotations.add.total")}</span>
								<span>
									<Price value={quotation.totalAmount} currency={currency} />
								</span>
							</div>
						</CardContent>
					</Card>
				</div>
			</div>
		</div>
	);
};

export default QuotationDetailPage;
